<script setup>
import { computed } from 'vue';

// shadcn-style dialog. One rule for the app: a dialog never opens another
// dialog. Pages own a single "which dialog is open" state; dialogs report
// back with events (e.g. `saved`) instead of a global event bus.
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    // 2xl is for two side-by-side panels (e.g. a record and a form); 3xl
    // for wide tables. On a smaller screen each still fits the window.
    validator: (v) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl'].includes(v),
  },
  // Persistent dialogs ignore outside clicks (use for forms with input).
  persistent: { type: Boolean, default: true },
  hideClose: { type: Boolean, default: false },
});

const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['close']);

const maxWidth = computed(
  () => ({ sm: 420, md: 512, lg: 672, xl: 896, '2xl': 1120, '3xl': 1400 })[props.size],
);

function close() {
  open.value = false;
  emit('close');
}
</script>

<template>
  <v-dialog
    v-model="open"
    :max-width="maxWidth"
    :persistent="persistent"
    scrim="black"
    :opacity="0.5"
    class="ui-dialog-overlay"
    @keydown.esc="close"
  >
    <div class="ui-dialog" role="document">
      <button
        v-if="!hideClose"
        type="button"
        class="ui-dialog__close"
        aria-label="Close"
        @click="close"
      >
        <v-icon size="18">mdi-close</v-icon>
      </button>

      <header class="ui-dialog__header">
        <!-- A custom header (e.g. a profile block) still gets the title in it. -->
        <slot name="header" :title="title" :description="description">
          <h2 class="ui-dialog__title">{{ title }}</h2>
          <p v-if="description" class="ui-dialog__description">
            {{ description }}
          </p>
        </slot>
      </header>

      <div v-if="$slots.default" class="ui-dialog__body">
        <slot :close="close" />
      </div>

      <footer v-if="$slots.footer" class="ui-dialog__footer">
        <slot name="footer" :close="close" />
      </footer>
    </div>
  </v-dialog>
</template>

<style scoped>
.ui-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(100vh - 32px);
  padding: 24px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  color: var(--foreground);
}

.ui-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}
.ui-dialog__close:hover {
  opacity: 1;
  background-color: var(--accent);
}
.ui-dialog__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(161, 161, 161, 0.5);
}

.ui-dialog__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 32px;
}
.ui-dialog__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
}
.ui-dialog__description {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--muted-foreground);
}

.ui-dialog__body {
  min-height: 0;
  overflow-y: auto;
  /* Room for focus rings at the edges of the scroll area */
  margin: 0 -4px;
  padding: 0 4px;
}

.ui-dialog__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
@media (max-width: 600px) {
  .ui-dialog__footer {
    flex-direction: column-reverse;
  }
  .ui-dialog__footer :deep(.v-btn) {
    width: 100%;
  }
}
</style>
